import { useTranslation } from 'react-i18next';
import { Paperclip, Upload } from 'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { FileUploader } from '@/shared/components/custom/file-uploader/FileUploader';
import { formatDisplayDate } from '@/shared/utils/dateUtils';
import { useActiveOrganization } from '@/features/organizations/public';
import { MemberRole } from '@/core/constants/roles';
import { useTimeOffRequest } from '../../hooks/useTimeOffRequest';
import { useUploadTimeOffAttachment } from '../../hooks/useUploadTimeOffAttachment';
import { useDownloadTimeOffAttachment } from '../../hooks/useDownloadTimeOffAttachment';
import { RequestStepper } from './RequestStepper';
import { CancelRequestAction } from './CancelRequestAction';
import { CancelDayAction } from './CancelDayAction';
import { OverrideDialog } from '../approvals/OverrideDialog';
import type { ResolutionMode } from '../../types';

interface RequestDetailProps {
  requestId: string;
}

const RESOLUTION_MODE_KEY: Record<ResolutionMode, string> = {
  Manual: 'requests.resolutionMode.manual',
  Timeout: 'requests.resolutionMode.timeout',
  AdminBypass: 'requests.resolutionMode.adminBypass',
  NoApproverAvailable: 'requests.resolutionMode.noApproverAvailable',
  ApprovalNotRequired: 'requests.resolutionMode.approvalNotRequired',
};

/**
 * `reason`/`attachments` are omitted entirely when the backend redacts them
 * (visibility below `Full`) — never rendered as an empty section, per the
 * integration doc's explicit warning about this.
 */
export function RequestDetail({ requestId }: RequestDetailProps) {
  const { t } = useTranslation('timeOff');
  const { data, isLoading, isError } = useTimeOffRequest(requestId);
  const uploadAttachment = useUploadTimeOffAttachment();
  const downloadAttachment = useDownloadTimeOffAttachment();
  const { activeOrganization } = useActiveOrganization();
  const isAdmin =
    activeOrganization?.role === MemberRole.Owner || activeOrganization?.role === MemberRole.Admin;

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="time-off-request-detail-loading-skeleton">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="time-off-request-detail-error">
        {t('requests.table.error')}
      </p>
    );
  }

  const hasReason = typeof data.reason === 'string' && data.reason.length > 0;
  const hasAttachments = (data.attachments?.length ?? 0) > 0;
  const resolutionModeKey = data.resolutionMode ? RESOLUTION_MODE_KEY[data.resolutionMode] : null;

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4" data-testid="time-off-request-detail">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge>{data.status}</Badge>
          {resolutionModeKey && (
            <span className="text-sm text-muted-foreground">{t(resolutionModeKey)}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {data.status === 'Pending' && isAdmin && <OverrideDialog requestId={requestId} />}
          {data.status === 'Pending' && <CancelRequestAction requestId={requestId} />}
        </div>
      </div>

      <div className="space-y-1 text-sm">
        {data.days?.map((day) => (
          <div key={day.id ?? day.date} className="flex items-center justify-between">
            <span
              className={day.status === 'Cancelled' ? 'text-muted-foreground line-through' : ''}
            >
              {formatDisplayDate(day.date)}
            </span>
            {data.status === 'Pending' && day.status === 'Active' && (
              <CancelDayAction requestId={requestId} date={day.date} disabled={day.date <= today} />
            )}
          </div>
        ))}
      </div>

      {hasReason && (
        <div className="space-y-1" data-testid="time-off-request-detail-reason">
          <p className="text-xs font-medium text-muted-foreground">{t('requests.detail.reason')}</p>
          <p className="text-sm">{data.reason}</p>
        </div>
      )}

      {hasAttachments && (
        <div className="space-y-1" data-testid="time-off-request-detail-attachments">
          <p className="text-xs font-medium text-muted-foreground">
            {t('requests.detail.attachments')}
          </p>
          <ul className="space-y-1 text-sm">
            {data.attachments?.map((attachment) => (
              <li key={attachment.id}>
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-primary hover:underline"
                  onClick={() =>
                    attachment.id &&
                    attachment.fileName &&
                    downloadAttachment.mutate({
                      requestId,
                      attachmentId: attachment.id,
                      fileName: attachment.fileName,
                    })
                  }
                  data-testid={`time-off-request-attachment-${attachment.id}`}
                >
                  <Paperclip className="size-3.5" />
                  {attachment.fileName}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <FileUploader
        multiple={false}
        onFilesAccepted={(files) =>
          files[0] && uploadAttachment.mutate({ requestId, file: files[0] })
        }
      >
        {({ getInputProps, open }) => (
          <>
            <input {...getInputProps()} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={open}
              disabled={uploadAttachment.isPending}
              data-testid="time-off-request-attachment-upload"
            >
              <Upload className="size-4" />
              {t('requests.form.attachment')}
            </Button>
          </>
        )}
      </FileUploader>

      {data.steps && data.steps.length > 0 && <RequestStepper steps={data.steps} />}
    </div>
  );
}
